"use client";

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { apiPost } from "@/lib/api";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { apiPublicGet } from "@/lib/api";
import Image from "next/image";

interface LoginResponse {
  access_token: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    role_id: string | null;
    department: string | null;
    permissions: import("@/lib/definitions").Permissions;
  };
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginContent />
    </Suspense>
  );
}

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, setIsPending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isMsLoading, setIsMsLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("expired") === "true") {
      // Fixed id: the effect can run more than once (StrictMode, searchParams
      // updates before the replace lands) — sonner keeps a single toast per id
      toast.error("Your session has expired. Please sign in again.", {
        id: "session-expired",
      });
      router.replace("/login", { scroll: false });
    }
  }, [searchParams, router]);

  async function handleMicrosoftLogin() {
    setIsMsLoading(true);
    try {
      const data = await apiPublicGet<{ auth_url: string }>(
        "/api/auth/microsoft/auth-url",
      );
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to start Microsoft sign-in",
      );
      setIsMsLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setIsPending(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    try {
      const data = await apiPost<LoginResponse>("/api/auth/login", {
        email,
        password,
      });

      // Store token and user info (including permissions) in cookies
      document.cookie = `token=${data.access_token}; path=/; max-age=${60 * 60 * 24 * 7}; samesite=lax`;
      document.cookie = `user=${encodeURIComponent(JSON.stringify(data.user))}; path=/; max-age=${60 * 60 * 24 * 7}; samesite=lax`;

      toast.success(`Welcome back, ${data.user.name}!`);
      // All users go to dashboard after login
      router.push("/dashboard");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Login failed");
    } finally {
      setIsPending(false);
    }
  }

  return (
    <Card className="w-full max-w-[400px] mx-4">
      <div className="flex flex-col items-center">
        <Image
          src="/small-logo.png"
          height={60}
          width={60}
          alt="Company Logo"
          unoptimized
        />
      </div>
      <CardHeader className="text-center pt-0">
        <CardTitle className="text-2xl font-semibold text-text-primary gap-0">
          EBM AIVAHR
        </CardTitle>
        <p className="text-sm text-text-secondary">
          Sign in to access the portal
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label
              htmlFor="email"
              className="text-sm font-medium text-text-primary"
            >
              Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          {/* password */}
          <div className="space-y-1">
            <label
              htmlFor="password"
              className="text-sm font-medium text-text-primary"
            >
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full text-text-on-accent"
            disabled={isPending}
          >
            {isPending ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">or</span>
          </div>
        </div>

        <Button
          variant="outline"
          className="w-full"
          onClick={handleMicrosoftLogin}
          disabled={isMsLoading}
        >
          {isMsLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
          )}
          Sign in with Microsoft
        </Button>
      </CardContent>
    </Card>
  );
}
